import type {
	EdgeMetadata,
	EdgeMetrics,
	EdgeStatsCumulative,
	EdgeStatsInterval,
	Jitter,
	TwilioEventRecord,
} from "@repo/common";
import {
	type CollectionReference,
	collection,
	type DocumentReference,
	doc,
	type Firestore,
	type FirestoreDataConverter,
	getDoc,
	type QueryDocumentSnapshot,
	type SnapshotOptions,
	Timestamp,
} from "firebase/firestore";

export {
	type Jitter,
	type EdgeStatsCumulative,
	type EdgeStatsInterval,
	type EdgeMetadata,
	type EdgeMetrics,
	type TwilioEventRecord,
};

const twilioEventConverter: FirestoreDataConverter<TwilioEventRecord> = {
	toFirestore(value) {
		return value.raw ?? {};
	},
	fromFirestore(snapshot: QueryDocumentSnapshot, options?: SnapshotOptions) {
		const data = snapshot.data(options) as Record<string, unknown>;
		const eventFields = readTwilioEventFields(data);

		return {
			id: eventFields.id ?? snapshot.id,
			occurredAt: eventFields.occurredAt,
			callSid: eventFields.callSid,
			accountSid: eventFields.accountSid,
			direction: eventFields.direction,
			edge: eventFields.edge,
			sipEdge: pickEdgeMetrics(data.sip_edge ?? data.sipEdge),
			carrierEdge: pickEdgeMetrics(data.carrier_edge ?? data.carrierEdge),
			raw: cloneRaw(data, eventFields.id ?? snapshot.id),
		};
	},
};

type TwilioEventFields = {
	id?: string;
	occurredAt: Date;
	callSid?: string;
	accountSid?: string;
	direction?: string;
	edge?: string;
};

function readTwilioEventFields(
	data: Record<string, unknown>,
): TwilioEventFields {
	if (isRecord(data.request) && isRecord(data.response)) {
		const parameters = isRecord(data.request.parameters)
			? data.request.parameters
			: undefined;
		return {
			id: ensureString(data.eventSid),
			occurredAt: parseDate(data.requestDateCreated),
			callSid: ensureString(parameters?.CallSid),
			accountSid: ensureString(parameters?.AccountSid),
			direction: ensureString(parameters?.Direction),
			edge: "monitor",
		};
	}

	return {
		id: ensureString(data.sid) ?? ensureString(data.event_sid),
		occurredAt: parseDate(data.timestamp),
		callSid: ensureString(data.call_sid),
		accountSid: ensureString(data.account_sid),
		direction: ensureString(data.direction),
		edge: ensureString(data.edge),
	};
}

export function twilioEventsCollection(
	db: Firestore,
): CollectionReference<TwilioEventRecord> {
	return collection(db, "twilioEvents").withConverter(twilioEventConverter);
}

export function twilioEventDocument(
	db: Firestore,
	eventId: string,
): DocumentReference<TwilioEventRecord> {
	return doc(db, "twilioEvents", eventId).withConverter(twilioEventConverter);
}

export async function fetchTwilioEvent(
	db: Firestore,
	eventId: string,
): Promise<TwilioEventRecord | null> {
	const snapshot = await getDoc(twilioEventDocument(db, eventId));
	return snapshot.exists() ? snapshot.data() : null;
}

export function compareTwilioEventRecords(
	a: TwilioEventRecord,
	b: TwilioEventRecord,
): number {
	return b.occurredAt.getTime() - a.occurredAt.getTime();
}

export function toTwilioUiEvent(record: TwilioEventRecord) {
	return {
		id: record.id,
		timestamp: formatDisplayTimestamp(record.occurredAt),
		account_sid: record.accountSid ?? "—",
		call_sid: record.callSid ?? "—",
		direction: record.direction ?? "unknown",
		edge: record.edge ?? "—",
		sip_edge: record.sipEdge,
		carrier_edge: record.carrierEdge,
		raw: record.raw,
	};
}

function pickEdgeMetrics(value: unknown): EdgeMetrics | undefined {
	if (!isRecord(value)) return undefined;

	const metadata = isRecord(value.metadata)
		? {
				edge_location: ensureString(value.metadata.edge_location) ?? "unknown",
				external_ip: ensureString(value.metadata.external_ip),
				region: ensureString(value.metadata.region) ?? "unknown",
				twilio_ip: ensureString(value.metadata.twilio_ip),
			}
		: undefined;

	if (!metadata) return undefined;

	const cumulative = isRecord(value.cumulative)
		? {
				codec_name: ensureString(value.cumulative.codec_name) ?? "unknown",
				jitter: parseJitter(value.cumulative.jitter) ?? {
					avg: 0,
					max: 0,
					min: 0,
				},
				packets_lost: pickFirstNumber(value.cumulative.packets_lost) ?? 0,
				packets_received: pickFirstNumber(value.cumulative.packets_received),
				packets_sent: pickFirstNumber(value.cumulative.packets_sent),
			}
		: undefined;

	const interval = isRecord(value.interval)
		? {
				codec_name: ensureString(value.interval.codec_name) ?? "unknown",
				jitter: parseJitter(value.interval.jitter) ?? {
					avg: 0,
					max: 0,
					min: 0,
				},
				packets_loss_percentage:
					pickFirstNumber(value.interval.packets_loss_percentage) ?? 0,
				packets_lost: pickFirstNumber(value.interval.packets_lost) ?? 0,
				packets_received: pickFirstNumber(value.interval.packets_received),
				packets_sent: pickFirstNumber(value.interval.packets_sent),
			}
		: undefined;

	const codec = pickFirstNumber(value.codec) ?? 0;
	const codec_name = ensureString(value.codec_name) ?? "unknown";

	if (!cumulative || !interval) return undefined;

	return {
		codec,
		codec_name,
		cumulative,
		interval,
		metadata,
	};
}

function ensureString(value: unknown): string | undefined {
	if (typeof value === "string" && value.trim().length > 0) {
		return value;
	}
	return undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

function parseDate(value: unknown): Date {
	if (value instanceof Date) {
		return value;
	}
	if (value instanceof Timestamp) {
		return value.toDate();
	}
	if (typeof value === "number") {
		const millis = value >= 1_000_000_000_000 ? value : value * 1000;
		return new Date(millis);
	}
	if (typeof value === "string") {
		const numericValue = Number(value);
		if (!Number.isNaN(numericValue)) {
			return parseDate(numericValue);
		}
		const parsed = Date.parse(value);
		if (!Number.isNaN(parsed)) {
			return new Date(parsed);
		}
	}
	if (isRecord(value) && typeof value.seconds === "number") {
		const millis =
			value.seconds * 1000 +
			(typeof value.nanoseconds === "number"
				? value.nanoseconds / 1_000_000
				: 0);
		return new Date(millis);
	}
	return new Date(0);
}

function pickFirstNumber(...values: unknown[]): number | undefined {
	for (const value of values) {
		if (typeof value === "number" && Number.isFinite(value)) {
			return value;
		}
		if (typeof value === "string") {
			const trimmed = value.trim();
			if (trimmed.length === 0) {
				continue;
			}
			const parsed = Number(trimmed);
			if (!Number.isNaN(parsed)) {
				return parsed;
			}
		}
	}
	return undefined;
}

function parseJitter(value: unknown): Jitter | undefined {
	if (!isRecord(value)) return undefined;

	const avg = pickFirstNumber(value.avg) ?? 0;
	const max = pickFirstNumber(value.max) ?? 0;
	const min = pickFirstNumber(value.min) ?? 0;

	return { avg, max, min };
}

function cloneRaw(
	value: Record<string, unknown>,
	id: string,
): Record<string, unknown> {
	try {
		return JSON.parse(JSON.stringify({ ...value, id })) as Record<
			string,
			unknown
		>;
	} catch {
		return { ...value, id };
	}
}

function formatDisplayTimestamp(date: Date): string {
	if (Number.isNaN(date.getTime())) {
		return "Unknown";
	}
	return date.toLocaleString();
}

export type TwilioEvent = ReturnType<typeof toTwilioUiEvent>;
